import { Button, CustomHeader, ErrorMessage, Screen, Text, TextInput } from "components"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Image, PermissionsAndroid, Platform, View } from "react-native"
import { style } from "./styles"
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import { translate } from "i18n"
import { useStores } from "models"
import { observer } from "mobx-react-lite"
import flash from "config/flash"
import Modal from "react-native-modal"
import { colors } from "colors"
import { ActivityIndicator } from "react-native-paper"
import { useForm, Controller } from "react-hook-form"
import { CameraOptions, launchCamera } from "react-native-image-picker"
import DropDownPicker from "react-native-dropdown-picker"
import { upperFirst } from "lodash"
import { useColor } from "app/theme/getThemes"

export const UploadReceiptScreen = observer(() => {
  const { companyTheme } = useColor()
  const navigation = useNavigation<any>()
  const { userStore } = useStores()

  const { control, setValue, clearErrors, reset } = useForm({
    reValidateMode: "onSubmit",
    defaultValues: {
      invoiceNumber: "",
      totalValue: "",
      platform: "",
    },
  })

  const [data, setData] = useState({
    invoiceNumber: "",
    totalValue: "",
    platform: "",
  })

  const [selectedImage, setSelectedImage] = useState(null)
  const [modal, setModal] = useState(false)
  const [idVisible, setIDVisible] = useState(false)

  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const [loading, setIsLoading] = useState(false)
  const [platformList] = useState([
    { label: upperFirst(translate("common.shopee")), value: "shopee" },
    { label: upperFirst(translate("common.lazada")), value: "lazada" },
    { label: upperFirst(translate("common.tiktok")), value: "tiktok" },
    { label: upperFirst(translate("common.zalora")), value: "zalora" },
    { label: upperFirst(translate("common.wow")), value: "wow" },
    { label: upperFirst(translate("common.others")), value: "others" },
  ])

  useEffect(() => {
    if (Platform.OS !== "web") {
      requestCameraPermission()
    } else {
      handleCameraLaunch()
    }
  }, [])

  useFocusEffect(
    useCallback(() => {
      return () => {
        stopCamera()
      }
    }, []),
  )

  const requestCameraPermission = async () => {
    try {
      const granted = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.CAMERA, {
        title: "App Camera Permission",
        message: "App needs access to your camera",
        buttonNeutral: "Ask Me Later",
        buttonNegative: "Cancel",
        buttonPositive: "OK",
      })
      if (granted === PermissionsAndroid.RESULTS.GRANTED) {
        console.log("Camera permission given")
      } else {
        console.log("Camera permission denied")
      }
    } catch (err) {
      console.warn(err)
    }
  }

  const handleCameraLaunch = () => {
    if (Platform.OS === "web") {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream
            videoRef.current.play()
          }
        })
        .catch((err) => console.error("Error accessing the camera", err))
    } else {
      const options: CameraOptions = {
        mediaType: "photo",
        includeBase64: false,
        maxHeight: 2000,
        maxWidth: 2000,
      }

      launchCamera(options, (response) => {
        console.log(response)

        if (response.didCancel) {
          console.log("User cancelled camera")
        } else if (response.errorCode) {
          console.log("Camera Error: ", response.errorMessage)
        } else {
          const imageUri = response.assets?.[0]?.uri // Accessing uri from the first asset
          if (imageUri) {
            setSelectedImage(imageUri)
            console.log(imageUri)
          } else {
            console.log("No image URI found")
          }
        }
      })
    }
  }

  const captureImage = async () => {
    setIsLoading(true)

    const context = canvasRef.current.getContext("2d")
    context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height)

    // Specify the quality parameter (0.0 to 1.0) for compression
    const quality = 1.0 // Adjust this value as needed
    const imageUri = canvasRef.current.toDataURL("image/jpeg", quality)

    stopCamera()
    setSelectedImage(imageUri)

    try {
      const response = await fetch(
        `https://asia-southeast1-symple-dekode.cloudfunctions.net/Base_64_OCR-1`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content: imageUri,
          }),
        },
      )

      if (!response.ok) {
        throw new Error("Network response was not ok " + response.statusText)
      }

      const datas = await response.json()

      console.log(datas)

      const invoiceNumber = datas.invoice_number
      const totalValue = datas.total_value

      if (invoiceNumber === null && totalValue === null) {
        flash("error", translate("uploadReceiptScreen.bothnotFound"))
        openModal()
      } else if (totalValue === null) {
        setData((prevData) => ({ ...prevData, invoiceNumber }))
        setValue("invoiceNumber", invoiceNumber)
      } else if (invoiceNumber === null) {
        openModal()
        setData((prevData) => ({ ...prevData, totalValue }))
        setValue("totalValue", totalValue)
      } else {
        setData({ invoiceNumber, totalValue })
        setValue("invoiceNumber", invoiceNumber)
        setValue("totalValue", totalValue)
      }
    } catch (err) {
      console.error("Error", err)
    } finally {
      setIsLoading(false)
    }
  }

  const stopCamera = () => {
    // Check if the videoRef is assigned to an active stream
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject
      stream.getTracks().forEach((track) => track.stop())
      videoRef.current.srcObject = null // Optionally clear the video source
    } else {
      console.log("No active camera stream found.")
    }
  }

  const reCaptureImage = async () => {
    setSelectedImage(null)
    reset({
      invoiceNumber: "",
      totalValue: "",
    })
    handleCameraLaunch()
  }

  const submitImage = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.API_URL}/api/member/sales/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invoice_id: data?.invoiceNumber,
          user_id: userStore?.id,
          total_amount: data?.totalValue,
          status: "pending",
          company_code: process.env.COMPANY_CODE,
        }),
      })

      if (response.ok) {
        flash("success", translate("uploadReceiptScreen.uploadSuccess"))
        navigation.navigate("employeeMenu")
      } else {
        flash("error", translate("uploadReceiptScreen.uploadFailed"))
      }
    } catch (err) {
      console.error("Error", err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (data.invoiceNumber) {
      setValue("invoiceNumber", data.invoiceNumber)
    }
    if (data.totalValue) {
      setValue("totalValue", data.totalValue)
    }
  }, [data, setValue])

  const openModal = () => {
    setModal(true)
  }

  const closeModal = () => {
    setModal(false)
  }

  return (
    <>
      <CustomHeader
        containerStyle={style.headerContainer}
        height={100}
        headerTitle={translate("uploadReceiptScreen.header")}
        loading={loading}
      />
      <Screen>
        <View style={style.container}>
          <View>
            <Controller
              name="platform"
              control={control}
              rules={{
                required: translate("errors.required"),
              }}
              defaultValue={"Shopee"}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <>
                    <View style={style.idTypeContainer}>
                      <View style={style.titleContainer}>
                        <Text style={style.title}>{translate("uploadReceiptScreen.platform")}</Text>
                      </View>
                      <DropDownPicker
                        style={[
                          style.dropdown,
                          { maxWidth: 800, width: "100%" },
                          error && style.errorTextField,
                        ]}
                        dropDownContainerStyle={{
                          borderColor: colors.inputFieldBorder,
                          backgroundColor: colors.placeholder,
                        }}
                        placeholderStyle={{
                          color: colors.placeholder,
                        }}
                        open={idVisible}
                        listMode="MODAL"
                        value={value}
                        items={platformList}
                        setOpen={() => {
                          setIDVisible(!idVisible)
                        }}
                        size={800}
                        onSelectItem={(e: any) => onChange(e?.value)}
                      />
                      {error && (
                        <ErrorMessage errorType={error.type} customMessage={error?.message} />
                      )}
                    </View>
                  </>
                )
              }}
            />

            <Controller
              name="invoiceNumber"
              control={control}
              defaultValue="invoiceNumber"
              rules={{
                required: translate("errors.required"),
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <View>
                    <TextInput
                      onFocus={() => clearErrors("invoiceNumber")}
                      value={value}
                      onChangeText={(text) => {
                        onChange(text)
                        setData((prevData) => ({ ...prevData, invoiceNumber: text }))
                      }}
                      error={error}
                      title={translate("uploadReceiptScreen.invoiceNumber")}
                      errorMessage={error?.message}
                      containerStyle={style.inputContainer}
                      style={style.inputStyle}
                    />
                  </View>
                )
              }}
            />

            <Controller
              name="totalValue"
              control={control}
              defaultValue="totalValue"
              rules={{
                required: translate("errors.required"),
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <View>
                    <TextInput
                      onFocus={() => clearErrors("totalValue")}
                      value={value}
                      onChangeText={(text) => {
                        onChange(text)
                        setData((prevData) => ({ ...prevData, totalValue: text }))
                      }}
                      error={error}
                      title={translate("uploadReceiptScreen.totalValue")}
                      errorMessage={error?.message}
                      containerStyle={style.inputContainer}
                      style={style.inputStyle}
                    />
                  </View>
                )
              }}
            />
          </View>

          {selectedImage ? (
            <View style={style.mediaContainer}>
              <Image
                source={{ uri: selectedImage }}
                style={style.imageStyle}
                resizeMode="stretch"
              />
            </View>
          ) : Platform.OS === "web" ? (
            <>
              <div style={style.videoContainer}>
                <video ref={videoRef} style={style.videoStyle} />
              </div>
              <canvas ref={canvasRef} style={style.canvasStyle} />
            </>
          ) : null}

          <View style={style.pt5}>
            {loading ? (
              <ActivityIndicator size="small" color={companyTheme.first} />
            ) : !selectedImage ? (
              <Button onPress={captureImage} title="Capture Image" />
            ) : (
              <>
                <Button style={style.mb40} onPress={reCaptureImage} title="Recapture Image" />
                <Button style={style.mb40} onPress={submitImage} title="Submit" />
              </>
            )}
          </View>
        </View>

        <Modal
          isVisible={modal}
          style={style.modalContainer}
          onBackdropPress={() => closeModal()}
          onBackButtonPress={() => closeModal()}
        >
          <View style={style.modalContentContainer}>
            <Text style={style.titleText}>{translate("uploadReceiptScreen.modalHeader")}</Text>
            <Image
              source={require("../../../../assets/exampleReceipt.png")}
              style={style.capturedImage}
            />
            <Button
              style={[style.modalContentButton,{borderColor: companyTheme.first}]}
              titleStyle={style.modalButton}
              onPress={closeModal}
              title={translate("uploadReceiptScreen.ok")}
            />
          </View>
        </Modal>
      </Screen>
    </>
  )
})
